const getDataLayer = () => {
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

const GTM_SESSION_STORAGE_KEY = "kip_gtm_session_id";

const toCountryCode = value => {
  if (!value || typeof value !== "string") return "";
  const first = value.split(",")[0] || "";
  return first.trim().toUpperCase();
};

export const getCentreSlug = centre => {
  if (!centre) return null;

  if (typeof centre === "string") {
    const slug = centre
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    return slug || null;
  }

  return getCentreSlug(centre.name);
};

const getGtmSessionId = () => {
  if (typeof window === "undefined" || !window.sessionStorage) return null;

  try {
    const existingSessionId = window.sessionStorage.getItem(GTM_SESSION_STORAGE_KEY);
    if (existingSessionId) return existingSessionId;

    const newSessionId = ["kip", Date.now(), Math.random().toString(36).slice(2, 10)].join("_");
    window.sessionStorage.setItem(GTM_SESSION_STORAGE_KEY, newSessionId);
    return newSessionId;
  } catch (error) {
    return null;
  }
};

export const pushTrackingEvent = (eventName, payload) => {
  const dataLayer = getDataLayer();
  if (!dataLayer) return;

  dataLayer.push({
    event: eventName,
    event_name: eventName,
    ...payload
  });
};

// ─── Booking Abandon State (module-level, private) ──────────────────────────

const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes — adjust if needed
const VISIBILITY_DELAY_MS = 15 * 1000; // 15 seconds delay for tab switching

const _abandonState = {
  countryCode: "",
  campaignId: 0,
  centreId: null,
  centreSlug: null,
  bookingStep: null,
  centreSelected: false
};

let _inactivityTimer = null;
let _visibilityTimer = null;
let _bookingStarted = false;
let _abandonFired = false;
let _completeFired = false;

export const updateAbandonBookingStep = bookingStep => {
  _abandonState.bookingStep = bookingStep || null;
};

export const updateAbandonCentreSelected = centreSelected => {
  _abandonState.centreSelected = typeof centreSelected === "boolean" ? centreSelected : false;
};

const _updateAbandonState = ({ countryCode, campaignId, centreId, centreSlug }) => {
  if (countryCode !== undefined) _abandonState.countryCode = countryCode;
  if (campaignId  !== undefined) _abandonState.campaignId  = campaignId;
  if (centreId    !== undefined) _abandonState.centreId    = centreId;
  if (centreSlug  !== undefined) _abandonState.centreSlug  = centreSlug;
};

const _fireAbandonOnce = () => {
  if (_abandonFired || _completeFired) return;
  if (!_bookingStarted) return;
  _abandonFired = true;
  clearTimeout(_inactivityTimer);
  clearTimeout(_visibilityTimer);
  trackBookingAbandon({
    countryCode: _abandonState.countryCode,
    campaignId:  _abandonState.campaignId,
    centreId:    _abandonState.centreId,
    centreSlug:  _abandonState.centreSlug,
    bookingStep: _abandonState.bookingStep,
    centreSelected: _abandonState.centreSelected
  });
};

const _resetInactivityTimer = () => {
  clearTimeout(_inactivityTimer);
  if (_completeFired || _abandonFired) return;
  _inactivityTimer = setTimeout(_fireAbandonOnce, INACTIVITY_TIMEOUT_MS);
};

const _handleVisibilityChange = () => {
  if (typeof document === "undefined") return;

  if (document.visibilityState === "hidden") {
    // Start a 15-second timer. If the user doesn't return, fire abandon.
    clearTimeout(_visibilityTimer);
    if (!_completeFired && !_abandonFired && _bookingStarted) {
      _visibilityTimer = setTimeout(_fireAbandonOnce, VISIBILITY_DELAY_MS);
    }
  } else if (document.visibilityState === "visible") {
    // If the user returns within 15 seconds, cancel the abandon timer.
    clearTimeout(_visibilityTimer);
  }
};

// ─── Page visibility / pagehide / exit listeners (registered once at module load) ──
if (typeof window !== "undefined") {
  // Capture clicks on out-of-funnel links (like header logos, main navigation, or footers)
  // to fire the abandonment event BEFORE the page actually unloads.
  document.addEventListener("click", (e) => {
    try {
      const anchor = e.target.closest("a");
      if (anchor && anchor.href) {
        const targetUrl = new URL(anchor.href, window.location.href);
        if (
          targetUrl.origin !== window.location.origin ||
          !targetUrl.pathname.includes("/book-assessment")
        ) {
          _fireAbandonOnce();
        }
      }
    } catch (err) {
      // Fail silently for non-standard/relative formats (e.g. tel:, mailto:)
    }
  }, { capture: true });

  document.addEventListener("beforeunload", _fireAbandonOnce);
  document.addEventListener("pagehide", _fireAbandonOnce);
  document.addEventListener("visibilitychange", _handleVisibilityChange);
}

// ────────────────────────────────────────────────────────────────────────────

export const trackBookingAddressSearch = ({
  countryCode,
  campaignId,
  searchTerm,
  searchResultCount,
  inCentreResultCount,
  onlineResultCount,
  selectedServiceType,
  hasLocalCentre,
  nearestCentreDistanceKm
}) => {
  if (typeof window === "undefined") return;

  const payload = {
    application_name: "mu-web",
    country_code: toCountryCode(countryCode),
    page_type: "booking",
    page_path: window.location.pathname,
    search_term: searchTerm || null,
    search_result_count: typeof searchResultCount === "number" ? searchResultCount : null,
    in_centre_result_count: typeof inCentreResultCount === "number" ? inCentreResultCount : null,
    online_result_count: typeof onlineResultCount === "number" ? onlineResultCount : null,
    selected_service_type: selectedServiceType || null,
    has_local_centre: typeof hasLocalCentre === "boolean" ? hasLocalCentre : null,
    nearest_centre_distance_km: typeof nearestCentreDistanceKm === "number" ? nearestCentreDistanceKm : null,
    campaign_id: campaignId || 0,
    gtm_session_id: getGtmSessionId()
  };

  pushTrackingEvent("booking_address_search", payload);
};

export const trackBookingStart = ({ countryCode, campaignId, selectedServiceType }) => {
  if (typeof window === "undefined") return;

  if (window.__kipBookingStartTracked) return;
  window.__kipBookingStartTracked = true;

  const payload = {
    application_name: "mu-web",
    country_code: toCountryCode(countryCode),
    page_type: "booking",
    page_path: window.location.pathname,
    centre_id: null,
    centre_slug: null,
    selected_service_type: selectedServiceType || null,
    campaign_id: campaignId || 0,
    gtm_session_id: getGtmSessionId()
  };

  pushTrackingEvent("booking_start", payload);
  _bookingStarted = true;
  _updateAbandonState({ countryCode, campaignId });
  _abandonState.bookingStep = "centre_selection";
  _resetInactivityTimer();
};

export const trackBookingComplete = ({
  countryCode,
  campaignId,
  centreId,
  centreSlug,
  selectedServiceType,
  selectedTimeSlot,
  centreSelected
}) => {
  if (typeof window === "undefined") return;

  _completeFired = true;
  clearTimeout(_inactivityTimer);

  const payload = {
    application_name: "mu-web",
    country_code: toCountryCode(countryCode),
    page_type: "booking",
    page_path: window.location.pathname,
    centre_id: centreId || null,
    centre_slug: centreSlug || null,
    selected_service_type: selectedServiceType || null,
    selected_time_slot: selectedTimeSlot || null,
    centre_selected: centreSelected !== undefined ? centreSelected : true,
    campaign_id: campaignId || 0,
    gtm_session_id: getGtmSessionId()
  };

  pushTrackingEvent("booking_complete", payload);
};

export const trackBookingCentreSelected = ({
  countryCode,
  campaignId,
  centreId,
  centreSlug,
  selectedServiceType
}) => {
  if (typeof window === "undefined") return;

  const payload = {
    application_name: "mu-web",
    country_code: toCountryCode(countryCode),
    page_type: "booking",
    page_path: window.location.pathname,
    centre_id: centreId ? String(centreId) : null,
    centre_slug: centreSlug || null,
    selected_service_type: selectedServiceType || null,
    campaign_id: campaignId || 0,
    gtm_session_id: getGtmSessionId()
  };

  pushTrackingEvent("booking_centre_selected", payload);
  _updateAbandonState({ countryCode, campaignId, centreId, centreSlug });
  _resetInactivityTimer();
};

export const trackBookingStepView = ({
  countryCode,
  campaignId,
  bookingStep,
  bookingStepNumber,
  centreId,
  centreSlug,
  availableSlotCount,
  centreSelected
}) => {
  if (typeof window === "undefined") return;

  const payload = {
    application_name: "mu-web",
    country_code: toCountryCode(countryCode),
    page_type: "booking",
    page_path: window.location.pathname,
    booking_step: bookingStep,
    booking_step_number: bookingStepNumber,
    centre_id: centreId ? String(centreId) : null,
    centre_slug: centreSlug || null,
    available_slot_count: typeof availableSlotCount === "number" ? availableSlotCount : null,
    centre_selected: typeof centreSelected === "boolean" ? centreSelected : null,
    campaign_id: campaignId || 0,
    gtm_session_id: getGtmSessionId()
  };

  pushTrackingEvent("booking_step_view", payload);
  _updateAbandonState({ countryCode, campaignId, centreId, centreSlug });
  _resetInactivityTimer();
};

export const trackBookingSubmitAttempt = ({
  countryCode,
  campaignId,
  centreId,
  centreSlug,
  bookingStep
}) => {
  if (typeof window === "undefined") return;

  const payload = {
    application_name: "mu-web",
    country_code: toCountryCode(countryCode),
    page_type: "booking",
    page_path: window.location.pathname,
    centre_id: centreId ? String(centreId) : null,
    centre_slug: centreSlug || null,
    booking_step: bookingStep || "student_details",
    campaign_id: campaignId || 0,
    gtm_session_id: getGtmSessionId()
  };

  pushTrackingEvent("booking_submit_attempt", payload);
};

export const trackBookingError = ({
  countryCode,
  campaignId,
  centreId,
  centreSlug,
  errorType,
  errorMessage,
  bookingStep
}) => {
  if (typeof window === "undefined") return;

  const payload = {
    application_name: "mu-web",
    country_code: toCountryCode(countryCode),
    page_type: "booking",
    page_path: window.location.pathname,
    centre_id: centreId ? String(centreId) : null,
    centre_slug: centreSlug || null,
    booking_step: bookingStep || "student_details",
    error_type: errorType,
    error_message: errorMessage,
    campaign_id: campaignId || 0,
    gtm_session_id: getGtmSessionId()
  };

  pushTrackingEvent("booking_error", payload);
};

export const markBookingFallbackSubmitted = () => {
  _completeFired = true;
  clearTimeout(_inactivityTimer);
  clearTimeout(_visibilityTimer);
};

const getFallbackContactFormPayload = ({
  countryCode,
  campaignId,
  centreId,
  centreSlug
}) => ({
  application_name: "mu-web",
  country_code: toCountryCode(countryCode),
  page_type: "booking",
  page_path: window.location.pathname,
  form_type: "booking_fallback_time_request",
  booking_step: "slot_selection",
  centre_id: centreId ? String(centreId) : null,
  centre_slug: centreSlug || null,
  campaign_id: campaignId || 0,
  gtm_session_id: getGtmSessionId()
});

export const trackFallbackContactFormStart = trackingContext => {
  if (typeof window === "undefined") return;

  pushTrackingEvent("contact_form_start", getFallbackContactFormPayload(trackingContext));
};

export const trackFallbackContactFormSubmitAttempt = trackingContext => {
  if (typeof window === "undefined") return;

  pushTrackingEvent("contact_form_submit_attempt", getFallbackContactFormPayload(trackingContext));
};

export const trackFallbackContactFormError = ({
  errorType,
  errorMessage,
  ...trackingContext
}) => {
  if (typeof window === "undefined") return;

  pushTrackingEvent("contact_form_error", {
    ...getFallbackContactFormPayload(trackingContext),
    error_type: errorType || "unknown_error",
    error_message: errorMessage || "Fallback form submission failed."
  });
};

export const trackFallbackContactFormSubmit = trackingContext => {
  if (typeof window === "undefined") return;

  pushTrackingEvent("contact_form_submit", getFallbackContactFormPayload(trackingContext));
};

export const trackBookingAbandon = ({
  countryCode,
  campaignId,
  centreId,
  centreSlug,
  bookingStep,
  centreSelected
}) => {
  if (typeof window === "undefined") return;

  const payload = {
    application_name: "mu-web",
    country_code: toCountryCode(countryCode),
    page_type: "booking",
    page_path: window.location.pathname,
    centre_id: centreId ? String(centreId) : null,
    centre_slug: centreSlug || null,
    booking_step: bookingStep || null,
    centre_selected: typeof centreSelected === "boolean" ? centreSelected : false,
    campaign_id: campaignId || 0,
    gtm_session_id: getGtmSessionId()
  };

  pushTrackingEvent("booking_abandon", payload);
};
